<script>
/**
 * 회원 자산 입력 다이얼로그.
 *
 * 역할:
 *   - 보유 현금(KRW)과 포인트(POINT)를 관리자가 직접 입력/수정한다.
 *   - 저장 시 PUT /api/v1/member/{userCode}/asset 호출.
 *   - 성공 시 부모 컴포넌트에 'updated' 이벤트를 emit하여 상세 재조회를 트리거한다.
 *
 * 사용 패턴:
 *   <MemberAssetDialog
 *     v-model="assetDialog"
 *     :user-code="member.userCode"
 *     :current-krw="krwAsset"
 *     :current-point="pointAsset"
 *     @updated="fetchDetail"
 *   />
 */
import { updateMemberAsset } from '@/api/member.js'

export default {
  name: 'MemberAssetDialog',

  props: {
    /** v-model 바인딩: 다이얼로그 표시 여부 */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /** 대상 회원코드 */
    userCode: {
      type: String,
      required: true,
    },
    /** 현재 보유 현금(KRW) — 다이얼로그 초기값으로 사용 */
    currentKrw: {
      type: Number,
      default: 0,
    },
    /** 현재 보유 포인트 — 다이얼로그 초기값으로 사용 */
    currentPoint: {
      type: Number,
      default: 0,
    },
  },

  emits: ['update:modelValue', 'updated'],

  data() {
    return {
      /** 입력 폼 데이터 */
      form: {
        krwAmount:   0,
        pointAmount: 0,
      },

      /** API 호출 중 여부 */
      loading: false,

      /** 서버 에러 메시지 */
      errorMsg: '',
    }
  },

  computed: {
    /** v-model 브릿지: 부모의 v-model 값을 직접 수정하지 않고 computed로 처리 */
    dialogVisible: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      },
    },

    /** 현금 유효성 검사 규칙 */
    krwRules() {
      return [
        v => v !== '' && v !== null && v !== undefined || '현금 금액을 입력해 주세요.',
        v => Number(v) >= 0 || '0 이상의 금액을 입력해 주세요.',
        v => Number.isInteger(Number(v)) || '정수만 입력 가능합니다.',
      ]
    },

    /** 포인트 유효성 검사 규칙 */
    pointRules() {
      return [
        v => v !== '' && v !== null && v !== undefined || '포인트를 입력해 주세요.',
        v => Number(v) >= 0 || '0 이상의 값을 입력해 주세요.',
        v => Number.isInteger(Number(v)) || '정수만 입력 가능합니다.',
      ]
    },
  },

  watch: {
    /**
     * 다이얼로그가 열릴 때마다 현재 값으로 폼을 초기화한다.
     * 이전 에러 메시지도 함께 초기화하여 잔류 오류 표시를 방지한다.
     */
    modelValue(newVal) {
      if (newVal) {
        this.form.krwAmount   = this.currentKrw
        this.form.pointAmount = this.currentPoint
        this.errorMsg = ''
      }
    },
  },

  methods: {
    /** 다이얼로그 닫기 (취소 버튼 또는 오버레이 클릭 시 호출) */
    handleClose() {
      this.dialogVisible = false
    },

    /**
     * 자산 저장.
     * 폼 유효성 검사 → API 호출 → 성공 시 updated 이벤트 emit.
     */
    async handleSubmit() {
      const { valid } = await this.$refs.assetForm.validate()
      if (!valid) return

      this.loading = true
      this.errorMsg = ''

      try {
        const res = await updateMemberAsset(this.userCode, {
          krwAmount:   Number(this.form.krwAmount),
          pointAmount: Number(this.form.pointAmount),
        })

        if (res.data?.code === 0) {
          // 성공: 부모에게 재조회 트리거 이벤트 전달 후 다이얼로그 닫기
          this.$emit('updated', res.data.data)
          this.dialogVisible = false
        } else {
          this.errorMsg = res.data?.message || '자산 저장에 실패했습니다.'
        }
      } catch (e) {
        console.error('[MemberAssetDialog] handleSubmit error:', e)
        this.errorMsg = '서버 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    },

    /** 숫자를 천 단위 콤마 형식으로 미리보기 (입력값 포맷 표시) */
    formatPreview(val) {
      const n = Number(val)
      if (isNaN(n) || val === '') return '-'
      return n.toLocaleString('ko-KR')
    },
  },
}
</script>

<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="480"
    persistent
  >
    <v-card rounded="xl" elevation="4">

      <!-- 다이얼로그 헤더 -->
      <div class="px-6 pt-5 pb-4 d-flex align-center ga-2">
        <div class="header-icon-wrap bg-amber-soft">
          <v-icon color="amber-darken-2" size="18">mdi-wallet-plus-outline</v-icon>
        </div>
        <div>
          <div class="text-subtitle-1 font-weight-bold">자산 입력</div>
          <div class="text-caption text-medium-emphasis">보유 현금과 포인트를 직접 입력합니다.</div>
        </div>
      </div>

      <v-divider />

      <!-- 입력 폼 -->
      <v-card-text class="pa-6">
        <v-form ref="assetForm">

          <!-- 현금(KRW) 입력 -->
          <div class="field-group">
            <div class="field-label">
              <v-icon size="14" class="mr-1" color="primary">mdi-cash-multiple</v-icon>
              보유 현금 (KRW)
            </div>
            <v-text-field
              v-model="form.krwAmount"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              type="number"
              min="0"
              step="1"
              placeholder="0"
              suffix="원"
              :rules="krwRules"
              hide-details="auto"
            />
            <!-- 입력값 미리보기 -->
            <div v-if="form.krwAmount !== '' && form.krwAmount >= 0" class="preview-text">
              → {{ formatPreview(form.krwAmount) }}원
            </div>
          </div>

          <!-- 포인트 입력 -->
          <div class="field-group">
            <div class="field-label">
              <v-icon size="14" class="mr-1" color="blue-darken-1">mdi-star-circle-outline</v-icon>
              보유 포인트
            </div>
            <v-text-field
              v-model="form.pointAmount"
              variant="outlined"
              density="comfortable"
              rounded="lg"
              type="number"
              min="0"
              step="1"
              placeholder="0"
              suffix="P"
              :rules="pointRules"
              hide-details="auto"
            />
            <!-- 입력값 미리보기 -->
            <div v-if="form.pointAmount !== '' && form.pointAmount >= 0" class="preview-text">
              → {{ formatPreview(form.pointAmount) }}P
            </div>
          </div>

          <!-- 안내 문구 -->
          <v-alert
            type="info"
            variant="tonal"
            density="compact"
            rounded="lg"
            class="mt-1"
            icon="mdi-information-outline"
          >
            <div class="text-caption">
              최종 보유 금액을 입력해 주세요.
            </div>
          </v-alert>

          <!-- 서버 에러 메시지 -->
          <v-alert
            v-if="errorMsg"
            type="error"
            variant="tonal"
            density="compact"
            rounded="lg"
            class="mt-3"
          >
            {{ errorMsg }}
          </v-alert>

        </v-form>
      </v-card-text>

      <v-divider />

      <!-- 액션 버튼 -->
      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          variant="text"
          :disabled="loading"
          @click="handleClose"
        >
          취소
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          rounded="lg"
          :loading="loading"
          prepend-icon="mdi-content-save-outline"
          @click="handleSubmit"
        >
          저장
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<style scoped>
/* ── 헤더 아이콘 ── */
.header-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
}

.bg-amber-soft {
  background: rgba(255, 193, 7, 0.14);
}

/* ── 입력 필드 그룹 ── */
.field-group {
  margin-bottom: 20px;
}

.field-label {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

/* ── 입력값 미리보기 ── */
.preview-text {
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.5);
  margin-top: 5px;
  padding-left: 4px;
}
</style>
